"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { Clock, ChevronLeft } from "lucide-react";
import { SiteNavbar } from "@/shared/ui/site-navbar";
import { SiteFooter } from "@/shared/ui/site-footer";
import type { Assessment } from "@/features/assessment/types";
import type { AppLocale } from "@/core/i18n/config";
import type { SiteNavbarContent, SiteFooterContent } from "@/shared/types/site-shell-content";
import { localizeHref } from "@/core/i18n/routing";
import { AssessmentQuiz } from "../components/assessment-quiz";
interface AssessmentTakePageProps {
  assessment: Assessment;
  navigation: SiteNavbarContent;
  footer: SiteFooterContent;
  locale: AppLocale;
}
export function AssessmentTakePage(t0) {
  const $ = _c(46);
  const {
    assessment,
    navigation,
    footer,
    locale
  } = t0;
  let t1;
  if ($[0] !== navigation) {
    t1 = <SiteNavbar content={navigation} />;
    $[0] = navigation;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== locale) {
    t2 = localizeHref(locale, "/assessment");
    $[2] = locale;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ChevronLeft className="h-4 w-4" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = locale === "hi" ? "\u0938\u092D\u0940 \u0905\u0938\u0947\u0938\u092E\u0947\u0902\u091F" : "All Assessments";
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = <Link href={t2} className="mb-8 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground">{t3}{t4}</Link>;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      opacity: 0,
      y: 16
    };
    t7 = {
      opacity: 1,
      y: 0
    };
    t8 = {
      duration: 0.6
    };
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[11] !== assessment.imageUrl || $[12] !== assessment.title) {
    t9 = <div className="relative h-48 w-full shrink-0 overflow-hidden rounded-2xl bg-muted lg:h-40 lg:w-56"><Image src={assessment.imageUrl} alt={assessment.title} fill={true} sizes="(max-width: 1024px) 100vw, 224px" className="object-cover" /></div>;
    $[11] = assessment.imageUrl;
    $[12] = assessment.title;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== assessment.category) {
    t10 = <span className="mb-2 inline-block text-[10px] font-bold tracking-[0.18em] text-primary uppercase">{assessment.category}</span>;
    $[14] = assessment.category;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== assessment.title) {
    t11 = <h1 className="mb-3 font-heading text-2xl leading-snug font-semibold text-foreground sm:text-3xl">{assessment.title}</h1>;
    $[16] = assessment.title;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== assessment.description) {
    t12 = <p className="mb-4 max-w-lg text-sm leading-relaxed text-muted-foreground">{assessment.description}</p>;
    $[18] = assessment.description;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Clock className="h-3.5 w-3.5" />;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== assessment.durationLabel || $[22] !== assessment.questions.length) {
    t14 = <span className="flex items-center gap-1.5 text-xs text-muted-foreground">{t13}{assessment.durationLabel} • {assessment.questions.length} questions</span>;
    $[21] = assessment.durationLabel;
    $[22] = assessment.questions.length;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t12 || $[27] !== t14) {
    t15 = <div>{t10}{t11}{t12}{t14}</div>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t15 || $[30] !== t9) {
    t16 = <motion.div initial={t6} animate={t7} transition={t8} className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-center lg:gap-10">{t9}{t15}</motion.div>;
    $[29] = t15;
    $[30] = t9;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="mb-10 border-b border-border" />;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== assessment || $[34] !== locale) {
    t18 = <AssessmentQuiz assessment={assessment} locale={locale} />;
    $[33] = assessment;
    $[34] = locale;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t16 || $[37] !== t18 || $[38] !== t5) {
    t19 = <main className="mx-auto max-w-4xl px-4 pt-28 pb-20 sm:px-6 lg:px-8">{t5}{t16}{t17}{t18}</main>;
    $[36] = t16;
    $[37] = t18;
    $[38] = t5;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== footer) {
    t20 = <SiteFooter content={footer} />;
    $[40] = footer;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t1 || $[43] !== t19 || $[44] !== t20) {
    t21 = <div className="relative min-h-screen overflow-x-hidden bg-background font-sans text-foreground">{t1}{t19}{t20}</div>;
    $[42] = t1;
    $[43] = t19;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  return t21;
}